import { supabase } from '@/integrations/supabase/client';

export interface AgentPerformanceData {
  summary: {
    total_agents: number;
    total_procurements: number;
    total_value: number;
    average_quality_score: number;
    average_completion_rate: number;
    average_rejection_rate: number;
    average_on_time_rate: number;
  };
  rankings: Array<{
    agent_id: string;
    agent_name: string;
    region: string;
    total_procurements: number;
    completed_procurements: number;
    completion_rate: number;
    rejection_rate: number;
    total_value: number;
    avg_delivery_time: number;
    quality_score: number;
    on_time_deliveries: number;
    late_deliveries: number;
  }>;
  regionalPerformance: Array<{
    region: string;
    total_agents: number;
    avg_quality_score: number;
    total_value: number;
  }>;
  deliveryPerformance: Array<{
    name: string;
    value: number;
  }>;
}

const getDateRangeFilter = (dateRange: string) => {
  const now = new Date();
  let startDate: Date;

  switch (dateRange) {
    case 'last_30_days':
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case 'last_3_months':
      startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      break;
    case 'last_6_months':
      startDate = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
      break;
    case 'this_year':
      startDate = new Date(now.getFullYear(), 0, 1);
      break;
    default:
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  }

  return startDate.toISOString();
};

export const getAgentPerformance = async (dateRange: string): Promise<AgentPerformanceData> => {
  const startDate = getDateRangeFilter(dateRange);

  // Get all agents
  const { data: agents, error: agentsError } = await supabase
    .from('agents')
    .select('id, name, region')
    .eq('status', 'active');

  if (agentsError) {
    console.error('Error fetching agents:', agentsError);
    throw agentsError;
  }

  // Get procurements for agents within date range
  const { data: procurements, error: procurementsError } = await supabase
    .from('procurements')
    .select(`
      id,
      source_id,
      source_type,
      status,
      total_amount,
      expected_delivery_date,
      delivery_date,
      created_at
    `)
    .eq('source_type', 'agent')
    .gte('created_at', startDate)
    .not('source_id', 'is', null);

  if (procurementsError) {
    console.error('Error fetching procurements:', procurementsError);
    throw procurementsError;
  }

  // Calculate performance metrics for each agent
  const agentMetrics = agents.map(agent => {
    const agentProcurements = procurements.filter(p => p.source_id === agent.id);
    
    const totalProcurements = agentProcurements.length;
    const completedProcurements = agentProcurements.filter(p => 
      ['completed', 'delivered'].includes(p.status)
    ).length;
    const rejectedProcurements = agentProcurements.filter(p => 
      p.status === 'rejected'
    ).length;
    
    const completionRate = totalProcurements > 0 ? (completedProcurements / totalProcurements) * 100 : 0;
    const rejectionRate = totalProcurements > 0 ? (rejectedProcurements / totalProcurements) * 100 : 0;
    
    const totalValue = agentProcurements.reduce((sum, p) => sum + (p.total_amount || 0), 0);
    
    // Calculate delivery performance
    const deliveredProcurements = agentProcurements.filter(p => 
      p.delivery_date && p.expected_delivery_date
    );
    
    let onTimeDeliveries = 0;
    let lateDeliveries = 0;
    let totalDeliveryTime = 0;
    
    deliveredProcurements.forEach(p => {
      if (p.delivery_date && p.expected_delivery_date) {
        const expectedDate = new Date(p.expected_delivery_date);
        const actualDate = new Date(p.delivery_date);
        const deliveryTime = (actualDate.getTime() - expectedDate.getTime()) / (1000 * 60 * 60 * 24);
        
        totalDeliveryTime += deliveryTime;
        
        if (deliveryTime <= 0) {
          onTimeDeliveries++;
        } else {
          lateDeliveries++;
        }
      }
    });
    
    const avgDeliveryTime = deliveredProcurements.length > 0 ? 
      totalDeliveryTime / deliveredProcurements.length : 0;
    
    // Calculate quality score (completion rate weighted by rejection rate)
    const qualityScore = completionRate * (1 - rejectionRate / 100);
    
    return {
      agent_id: agent.id,
      agent_name: agent.name,
      region: agent.region,
      total_procurements: totalProcurements,
      completed_procurements: completedProcurements,
      completion_rate: completionRate,
      rejection_rate: rejectionRate,
      total_value: totalValue,
      avg_delivery_time: avgDeliveryTime,
      quality_score: qualityScore,
      on_time_deliveries: onTimeDeliveries,
      late_deliveries: lateDeliveries,
    };
  });

  // Sort by quality score (descending)
  const rankings = agentMetrics.sort((a, b) => b.quality_score - a.quality_score);

  // Calculate summary statistics
  const summary = {
    total_agents: agents.length,
    total_procurements: procurements.length,
    total_value: agentMetrics.reduce((sum, agent) => sum + agent.total_value, 0),
    average_quality_score: agentMetrics.length > 0 ? 
      agentMetrics.reduce((sum, agent) => sum + agent.quality_score, 0) / agentMetrics.length : 0,
    average_completion_rate: agentMetrics.length > 0 ? 
      agentMetrics.reduce((sum, agent) => sum + agent.completion_rate, 0) / agentMetrics.length : 0,
    average_rejection_rate: agentMetrics.length > 0 ? 
      agentMetrics.reduce((sum, agent) => sum + agent.rejection_rate, 0) / agentMetrics.length : 0,
    average_on_time_rate: agentMetrics.length > 0 ? 
      agentMetrics.reduce((sum, agent) => {
        const totalDeliveries = agent.on_time_deliveries + agent.late_deliveries;
        return sum + (totalDeliveries > 0 ? (agent.on_time_deliveries / totalDeliveries) * 100 : 0);
      }, 0) / agentMetrics.length : 0,
  };

  // Calculate regional performance
  const regionMap = new Map<string, { agents: number; totalQuality: number; totalValue: number }>();
  
  agentMetrics.forEach(agent => {
    const region = agent.region;
    if (!regionMap.has(region)) {
      regionMap.set(region, { agents: 0, totalQuality: 0, totalValue: 0 });
    }
    const regionData = regionMap.get(region)!;
    regionData.agents++;
    regionData.totalQuality += agent.quality_score;
    regionData.totalValue += agent.total_value;
  });

  const regionalPerformance = Array.from(regionMap.entries()).map(([region, data]) => ({
    region,
    total_agents: data.agents,
    avg_quality_score: data.agents > 0 ? data.totalQuality / data.agents : 0,
    total_value: data.totalValue,
  }));

  // Calculate overall delivery performance
  const totalOnTime = agentMetrics.reduce((sum, agent) => sum + agent.on_time_deliveries, 0);
  const totalLate = agentMetrics.reduce((sum, agent) => sum + agent.late_deliveries, 0);
  const totalDeliveries = totalOnTime + totalLate;

  const deliveryPerformance = [
    {
      name: 'On Time',
      value: totalDeliveries > 0 ? Math.round((totalOnTime / totalDeliveries) * 100) : 0,
    },
    {
      name: 'Late',
      value: totalDeliveries > 0 ? Math.round((totalLate / totalDeliveries) * 100) : 0,
    },
  ];

  return {
    summary,
    rankings,
    regionalPerformance,
    deliveryPerformance,
  };
};